/**
 * Launch Hardening — Priority Visual Queue
 *
 * Single source of truth for "strong" visual overlays (cinematic moments,
 * world status banners, hype shockwaves, nation war prompts, sponsor pops).
 *
 * Goals:
 *   - Cap to MAX_STRONG simultaneous heavy elements (default 2, 1 on low-end).
 *   - Smart cooldown to avoid emotional chaos.
 *   - Priority scoring with danger-mode boost.
 *   - Mobile-first: external consumers ask `canShow(channel, weight)`.
 *
 * Pure JS, no React deps in the engine.
 */
import { useSyncExternalStore } from "react";

export type VisualChannel =
  | "cinematic_moment"
  | "world_status"
  | "hype_shockwave"
  | "nation_war"
  | "sponsor_pop"
  | "victory_card"
  | "global_event";

export type VisualPriority = "low" | "normal" | "high" | "critical";

const PRIORITY_SCORE: Record<VisualPriority, number> = {
  low: 10, normal: 30, high: 60, critical: 100,
};

const COOLDOWN_MS: Record<VisualChannel, number> = {
  cinematic_moment: 9_000,
  world_status: 6_000,
  hype_shockwave: 12_000,
  nation_war: 15_000,
  sponsor_pop: 25_000,
  victory_card: 8_000,
  global_event: 10_000,
};

type Slot = { id: string; channel: VisualChannel; score: number; createdAt: number };

let MAX_STRONG = 2;
let dangerActive = false;
const active = new Map<string, Slot>();
const lastByChannel = new Map<VisualChannel, number>();
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
// Cached snapshot reference — useSyncExternalStore relies on Object.is equality.
// Returning a fresh object literal each call causes infinite re-renders in
// any consumer that schedules state updates in an effect (e.g. WarRoom tick).
let snapCache = { count: 0, max: 2, danger: false };
const snap = () => {
  if (
    snapCache.count !== active.size ||
    snapCache.max !== MAX_STRONG ||
    snapCache.danger !== dangerActive
  ) {
    snapCache = { count: active.size, max: MAX_STRONG, danger: dangerActive };
  }
  return snapCache;
};

export function setMaxStrong(n: number) {
  const next = Math.max(1, Math.min(3, n));
  if (next === MAX_STRONG) return;
  MAX_STRONG = next;
  emit();
}

export function setDangerActive(active: boolean) {
  if (dangerActive === active) return;
  dangerActive = active;
  emit();
}

function score(channel: VisualChannel, priority: VisualPriority): number {
  let s = PRIORITY_SCORE[priority];
  if (dangerActive && (channel === "cinematic_moment" || channel === "world_status" || channel === "hype_shockwave")) {
    s += 25;
  }
  return s;
}

/** Reserve a strong-visual slot. Returns null if rejected. */
export function acquireSlot(channel: VisualChannel, priority: VisualPriority = "normal", id?: string): string | null {
  const now = Date.now();
  const last = lastByChannel.get(channel) ?? 0;
  if (now - last < COOLDOWN_MS[channel]) return null;

  const incoming = score(channel, priority);
  if (active.size >= MAX_STRONG) {
    // Try to evict the weakest active slot if incoming has clearly higher score.
    let weakest: Slot | null = null;
    for (const slot of active.values()) {
      if (!weakest || slot.score < weakest.score) weakest = slot;
    }
    if (!weakest || incoming <= weakest.score + 10) return null;
    active.delete(weakest.id);
  }

  const slotId = id ?? `${channel}-${now}-${Math.random().toString(36).slice(2, 7)}`;
  active.set(slotId, { id: slotId, channel, score: incoming, createdAt: now });
  lastByChannel.set(channel, now);
  emit();
  return slotId;
}

export function releaseSlot(id: string | null | undefined) {
  if (!id) return;
  if (active.delete(id)) emit();
}

export function canShow(channel: VisualChannel, priority: VisualPriority = "normal"): boolean {
  const now = Date.now();
  const last = lastByChannel.get(channel) ?? 0;
  if (now - last < COOLDOWN_MS[channel]) return false;
  if (active.size < MAX_STRONG) return true;
  const incoming = score(channel, priority);
  for (const slot of active.values()) if (incoming > slot.score + 10) return true;
  return false;
}

export function useVisualQueueSnapshot() {
  return useSyncExternalStore(subscribe, snap, snap);
}
